import { createContext, use, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useRecordSosSession, useSos } from '@lb/domain'
import { useTranslation } from '@lb/i18n'
import { StormOverlay } from '@lb/ui'

import { http } from '../../lib/client'

const StormContext = createContext<{ open: () => void } | null>(null)

/**
 * The Emotional Storm overlay, mounted above the router (spec §12).
 *
 * Above the router deliberately: opening the SOS must not be a navigation. A
 * parent in a hard moment should not lose the page they were on, and the
 * overlay must be reachable from every screen including one that is mid-error.
 *
 * `useSos` is prefetched with a bundled fallback as placeholder data, so the
 * overlay has content from the first frame whatever the network is doing — the
 * client's half of the backend's rule that "a parent who opens the overlay must
 * be shown something".
 */
export function StormProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()
  const { data } = useSos(http)
  const record = useRecordSosSession(http)

  /*
   * The write fires on OPEN and its result is discarded.
   *
   * Deliberately in this order: `setOpen(true)` first, so the overlay is up
   * whatever the network does. A parent reaching for the SOS is in the one
   * moment the product must not make them wait on a request, and `mutate`
   * (not `mutateAsync`) with no error branch means a failed record is silent
   * — the overlay is the point, the counter is not.
   *
   * `useMemo` with an empty dep list would close over a stale `record`, so the
   * mutation object is a dependency even though React Query keeps it stable.
   */
  const value = useMemo(
    () => ({
      open: () => {
        setOpen(true)
        record.mutate()
      },
    }),
    [record],
  )

  return (
    <StormContext value={value}>
      {children}
      {data ? (
        <StormOverlay
          open={open}
          onOpenChange={setOpen}
          steps={data.steps}
          chips={data.chips}
          breathing={data.breathing}
          resources={data.resources}
          labels={{
            title: t('storm.open'),
            step: (n, total) => t('storm.step', { n, total }),
            next: t('storm.next'),
            back: t('storm.back'),
            done: t('storm.done'),
            close: t('storm.close'),
            inhale: t('storm.inhale'),
            hold: t('storm.hold'),
            exhale: t('storm.exhale'),
            cycleOf: (n, total) => t('storm.cycleOf', { n, total }),
            resources: t('storm.resources'),
          }}
        />
      ) : null}
    </StormContext>
  )
}

export function useStorm() {
  const value = use(StormContext)
  if (!value) throw new Error('useStorm must be used inside <StormProvider>')
  return value
}
